/**
 * Phase 2L-A50 — Related Articles strip for /blog/{slug}.
 *
 * Ranks candidates by: same category → shared tags → most-recent
 * fallback. The matching logic lives on the backend
 * (`GET /api/blog/posts/{slug}/related`) so we never expose draft
 * posts here — the endpoint filters to `status=published` and
 * excludes the current article.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function RelatedArticles({ slug }) {
  const [items, setItems] = useState(null); // null = loading, [] = none

  useEffect(() => {
    if (!slug) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/blog/posts/${encodeURIComponent(slug)}/related?limit=3`,
          { signal: ctrl.signal },
        );
        if (ctrl.signal.aborted) return;
        if (!r.ok) {
          setItems([]);
          return;
        }
        const data = await r.json();
        if (ctrl.signal.aborted) return;
        setItems(Array.isArray(data.items) ? data.items : []);
      } catch {
        if (!ctrl.signal.aborted) setItems([]);
      }
    })();
    return () => ctrl.abort();
  }, [slug]);

  // Hide the strip entirely if nothing related could be found — better
  // than showing an awkward empty heading.
  if (items === null || items.length === 0) return null;

  return (
    <section
      className="mt-12 pt-10 border-t border-slate-200"
      aria-labelledby="related-articles-heading"
      data-testid="related-articles"
    >
      <div className="flex items-baseline justify-between gap-4 mb-5">
        <h2
          id="related-articles-heading"
          className="font-display text-2xl sm:text-3xl font-bold text-slate-900"
        >
          Related articles
        </h2>
        <Link
          to="/blog"
          className="text-sm text-[#1F4E79] hover:underline whitespace-nowrap"
          data-testid="related-articles-all-link"
        >
          See all →
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((p) => (
          <RelatedCard key={p.slug} post={p} />
        ))}
      </div>
    </section>
  );
}

function RelatedCard({ post }) {
  const heroSrc = post.featured_image_url || "/img/v2/blog-fallback.jpg";
  return (
    <Link
      to={`/blog/${encodeURIComponent(post.slug)}`}
      className="group block rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-cyan-300 hover:shadow-md transition overflow-hidden"
      data-testid={`related-card-${post.slug}`}
    >
      <div className="aspect-[16/10] overflow-hidden bg-slate-100">
        <img
          src={heroSrc}
          alt={post.featured_image_url ? post.title : ""}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          onError={(e) => {
            // If a stale/remote featured image fails to load, fall back to
            // our in-house calm hero so the card never shows broken alt-text.
            if (e.currentTarget.src.indexOf("/img/v2/blog-fallback.jpg") === -1) {
              e.currentTarget.src = "/img/v2/blog-fallback.jpg";
            }
          }}
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-cyan-700 font-bold mb-2">
          {post.category ? <span>{post.category}</span> : <span className="text-slate-400">Article</span>}
          {post.published_at && (
            <>
              <span className="text-slate-300 normal-case tracking-normal">·</span>
              <span className="text-slate-500 normal-case tracking-normal text-xs font-medium">{fmtDate(post.published_at)}</span>
            </>
          )}
        </div>
        <h3 className="font-display text-lg font-bold text-slate-900 leading-snug group-hover:text-[#1F4E79] transition">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}
